import { useEffect } from 'react';
import { injectHeadScript } from '../util/inject-head-script';

interface Props {
    id: string;
}

/** `window` augmented with the queue stub Snitcher's loader expects. */
type SnitcherStub = {
    (...args: unknown[]): void;
    q?: unknown[];
    l?: number;
};

type SnitcherWindow = Window &
    typeof globalThis & {
        SnitchObject?: string;
        snid?: SnitcherStub;
    };

const SCRIPT_ID = 'snitcher-script';

/**
 * Snitcher loader, injected after hydration.
 *
 * The vendor snippet bootstraps itself with `insertBefore` against the first
 * `<script>` in the document. Server-rendered, that runs during parse and moves
 * the nodes React is about to hydrate — the same hazard that keeps the head
 * pixels out of the server-rendered `<head>` (see `injectHeadScript`). Queuing
 * the stub and loading the script from an effect leaves the hydrated tree alone.
 *
 * `snid` queues calls until the vendor script replaces it, so `verify` can be
 * issued before the load finishes.
 *
 * The bootstrap runs only on the inject that actually added the script, gated
 * on `injectHeadScript`'s return value.
 * The effect can re-run — the third-party-sharing gate in `BodyPixels` unmounts
 * this component when consent changes, and StrictMode double-invokes in dev —
 * and by then `window.snid` is the real vendor function, so an ungated re-run
 * would stamp `l` onto the vendor object and send a second `verify`.
 */
export const SnitcherBody: React.FC<Props> = ({ id }) => {
    useEffect(() => {
        const w = window as SnitcherWindow;
        w.SnitchObject = 'snid';
        if (!w.snid) {
            // Queues the raw `arguments` object, not a rest array: the vendor
            // script replays `q` after it loads and expects the shape its own
            // snippet would have produced.
            const stub = function snidStub() {
                // eslint-disable-next-line prefer-rest-params
                (stub.q = stub.q || []).push(arguments);
            } as SnitcherStub;
            w.snid = stub;
            w.snid.l = Date.now();
        }
        if (injectHeadScript(`https://snid.snitcher.com/${id}.js`, SCRIPT_ID)) {
            w.snid('verify', id);
        }
    }, [id]);

    return null;
};
